import t from 'react-translate';
import { useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import { getMentoringProgramAliases } from 'redux/selectors/mentoring-programs';
import NvCheckbox from 'shared/components/inputs/nv-checkbox';
import { css } from '@emotion/react';
import { gray2, gray4, gray7 } from 'styles/global_defaults/colors';
import { novoEdBlack, novoEdBlue } from 'athena/styles/colors';
import { halfSpacing } from 'styles/global_defaults/scaffolding';
import NvTooltip from 'shared/components/nv-tooltip';
import NvIcon from 'shared/components/nv-icon';
import NvPopover from 'shared/components/nv-popover';
import NvConfirmationPopover from 'shared/components/nv-confirmation-popover';
import { config } from '@config/pendo.config.json';
import MentoringProgramContext from '../../context';
import { overlayStyles } from './styles';

type MentorCapacitySettingProps = {
  name: string;
  onSubmit: () => void;
};

const styles = (isDisabled: boolean) => css`
  padding-top: ${halfSpacing}px;

  .mentor-capacity-input {
    &:checked + label:before {
      background: ${gray7};
      border-color: ${gray4};
      color: ${gray2};
    }
  }
  .mentor-capacity-label {
    color: ${isDisabled ? gray4 : novoEdBlack};
  }
  .mentor-capacity-info {
    color: ${novoEdBlue};
  }
`;

const MentorCapacitySetting = ({
  name,
  onSubmit,
}: MentorCapacitySettingProps) => {
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const mentoringProgramAliases = useSelector((state) => getMentoringProgramAliases(state, mentoringProgram.id));
  const { setValue, getValues } = useFormContext();
  const { maxMentees, mentorCapacityEnabled } = getValues();
  const translationBase = t.MENTORING_PROGRAMS.SETTINGS.TABS.PROGRAM_SETUP.MENTOR_CAPACITY;
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleConfirm = () => {
    setValue(name, !mentorCapacityEnabled);
    onSubmit();
    setShowConfirmation(false);
  };

  const handleChange = () => {
    if (mentorCapacityEnabled) {
      setShowConfirmation(true);
    } else {
      handleConfirm();
    }
  };

  const dataQATag = config.pendo.athena.mentorshipProgram.settings.programSetup
    .mentorCapacityCheckbox;

  const isDisabled = maxMentees <= 1;

  return (
    <div className='d-flex align-items-center' css={styles(isDisabled)}>
      <NvPopover
        enabled
        show={showConfirmation}
        overlayStyles={overlayStyles}
        content={(
          <NvConfirmationPopover
            onCancel={() => setShowConfirmation(false)}
            onConfirm={handleConfirm}
            header={translationBase.WARNING.TITLE()}
            message={translationBase.WARNING.CONTENT({
              mentorsAlias: mentoringProgramAliases.mentorsAlias,
              maxMentees,
            })}
            cancelBtnText={t.FORM.CANCEL()}
            confirmBtnText={t.FORM.OK()}
          />
        )}
        placement='left'
        rootClose
        onHide={() => setShowConfirmation(false)}
        className='d-inline-flex mw-100'
        preventOverflow
      >
        <NvCheckbox
          name={name}
          checked={mentorCapacityEnabled}
          inputClassName='mentor-capacity-input'
          labelClassName='text-regular mentor-capacity-label'
          label={translationBase.TITLE(mentoringProgramAliases)}
          onChange={handleChange}
          dataQa={dataQATag}
          disabled={isDisabled}
        />
      </NvPopover>
      <NvTooltip text={translationBase.INFO(mentoringProgramAliases)}>
        <NvIcon
          icon='info'
          className='mentor-capacity-info ml-1'
          size='xss-smallest'
          tabIndex={0}
        />
      </NvTooltip>
    </div>
  );
};

export default MentorCapacitySetting;
